import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, qs } from '../api';
import { Icon } from '../components/icons';
import { Empty, ErrorBox, Field, ItemStatusBadge, Loading, Modal, PageHead } from '../components/ui';
import { useT } from '../i18n';
import type { Item, Kit } from '../types';
import { useOpenLabels } from './Equipment';

export function KitsPage() {
  const t = useT();
  const navigate = useNavigate();
  const openLabels = useOpenLabels();
  const [adding, setAdding] = useState(false);
  const kits = useQuery({ queryKey: ['kits'], queryFn: () => api.get<Kit[]>('/kits') });
  const list = kits.data ?? [];
  return (
    <>
      <PageHead
        title={t('kits.title')}
        sub={t('kits.sub')}
        actions={
          <>
            <button className="btn" disabled={!list.length} onClick={() => openLabels(list.map((k) => ({ code: k.qrCode, name: k.name })))}>
              <Icon.print />
              {t('kits.printCaseLabels')}
            </button>
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              <Icon.plus />
              {t('kits.new')}
            </button>
          </>
        }
      />
      <div className="card table-wrap">
        {kits.isLoading ? (
          <Loading />
        ) : list.length === 0 ? (
          <Empty title={t('kits.emptyTitle')} text={t('kits.emptyText')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('equipment.code')}</th>
                <th>{t('kits.name')}</th>
                <th className="right">{t('kits.items')}</th>
                <th>{t('common.notes')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((k) => (
                <tr key={k.id} className="clickable" onClick={() => navigate(`/kits/${k.id}`)}>
                  <td className="mono">{k.qrCode}</td>
                  <td><Link to={`/kits/${k.id}`}>{k.name}</Link></td>
                  <td className="right num">{k.itemCount}</td>
                  <td className="muted">{k.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && <KitFormModal onClose={() => setAdding(false)} />}
    </>
  );
}

function KitFormModal({ kit, onClose }: { kit?: Kit; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(kit?.name ?? '');
  const [notes, setNotes] = useState(kit?.notes ?? '');
  const save = useMutation({
    mutationFn: () => (kit ? api.patch<Kit>(`/kits/${kit.id}`, { name, notes: notes || null }) : api.post<Kit>('/kits', { name, notes: notes || null })),
    onSuccess: (k) => {
      qc.invalidateQueries({ queryKey: ['kits'] });
      qc.invalidateQueries({ queryKey: ['kit', k.id] });
      onClose();
      if (!kit) navigate(`/kits/${k.id}`);
    },
  });
  return (
    <Modal
      title={kit ? t('kits.edit') : t('kits.new')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!name || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <Field label={t('kits.name')} hint={t('kits.nameHint')} htmlFor="k-name">
        <input id="k-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label={t('common.notes')} htmlFor="k-notes">
        <textarea id="k-notes" className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

export function KitDetailPage() {
  const { id } = useParams();
  const t = useT();
  const qc = useQueryClient();
  const openLabels = useOpenLabels();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const kit = useQuery({ queryKey: ['kit', id], queryFn: () => api.get<Kit>(`/kits/${id}`) });
  const remove = useMutation({
    mutationFn: (itemId: string) => api.del(`/kits/${id}/items/${itemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kit', id] });
      qc.invalidateQueries({ queryKey: ['kits'] });
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
  if (kit.isLoading) return <Loading />;
  if (!kit.data) return <ErrorBox error={kit.error} />;
  const k = kit.data;
  const items = k.items ?? [];
  return (
    <>
      <PageHead
        back={{ to: '/kits', label: t('nav.kits') }}
        title={k.name}
        sub={<span className="mono">{k.qrCode}</span>}
        actions={
          <>
            <button className="btn" onClick={() => openLabels([{ code: k.qrCode, name: k.name }])}>
              <Icon.print />
              {t('kits.printCaseLabel')}
            </button>
            <button className="btn" disabled={!items.length} onClick={() => openLabels(items.map((i) => ({ code: i.qrCode, name: i.name })))}>
              <Icon.print />
              {t('kits.printItemLabels', { n: items.length })}
            </button>
            <button className="btn" onClick={() => setEditing(true)}>{t('common.edit')}</button>
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              <Icon.plus />
              {t('kits.addItems')}
            </button>
          </>
        }
      />
      {k.notes && <p className="muted">{k.notes}</p>}
      <ErrorBox error={remove.error} />
      <div className="card table-wrap">
        {items.length === 0 ? (
          <Empty title={t('kits.noItems')} text={t('kits.noItemsText')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('equipment.code')}</th>
                <th>{t('equipment.name')}</th>
                <th>{t('equipment.category')}</th>
                <th>{t('common.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id}>
                  <td className="mono">{i.qrCode}</td>
                  <td><Link to={`/equipment/${i.id}`}>{i.name}</Link></td>
                  <td>{i.category}</td>
                  <td><ItemStatusBadge status={i.status} /></td>
                  <td className="right">
                    <button className="btn btn-sm btn-ghost" onClick={() => remove.mutate(i.id)} disabled={remove.isPending}>
                      {t('kits.removeFromCase')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <KitFormModal kit={k} onClose={() => setEditing(false)} />}
      {adding && <AddItemsToKitModal kitId={k.id} onClose={() => setAdding(false)} />}
    </>
  );
}

function AddItemsToKitModal({ kitId, onClose }: { kitId: string; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const items = useQuery({ queryKey: ['items', { search }], queryFn: () => api.get<Item[]>(`/items${qs({ search })}`) });
  // Only items that are not in any case yet.
  const free = useMemo(() => (items.data ?? []).filter((i) => !i.kitId).slice(0, 300), [items.data]);
  const add = useMutation({
    mutationFn: () => api.post(`/kits/${kitId}/items`, { itemIds: [...selected] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kit', kitId] });
      qc.invalidateQueries({ queryKey: ['kits'] });
      qc.invalidateQueries({ queryKey: ['items'] });
      onClose();
    },
  });
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  return (
    <Modal
      wide
      title={t('kits.addItems')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!selected.size || add.isPending} onClick={() => add.mutate()}>
            {t('kits.addN', { n: selected.size })}
          </button>
        </>
      }
    >
      <ErrorBox error={add.error} />
      <div className="row">
        <input className="input" type="search" placeholder={t('equipment.search')} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        <button className="btn btn-sm" onClick={() => setSelected(new Set(free.map((i) => i.id)))} disabled={!free.length}>
          {t('common.selectAllN', { n: free.length })}
        </button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>{t('kits.onlyFree')}</p>
      <div className="table-wrap" style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
        {items.isLoading ? (
          <Loading />
        ) : free.length === 0 ? (
          <div className="empty">{t('kits.noFreeItems')}</div>
        ) : (
          <table className="table">
            <tbody>
              {free.map((i) => (
                <tr key={i.id} className="clickable" onClick={() => toggle(i.id)}>
                  <td className="check"><input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} onClick={(e) => e.stopPropagation()} aria-label={i.qrCode} /></td>
                  <td className="mono">{i.qrCode}</td>
                  <td>{i.name}</td>
                  <td className="muted">{i.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
